import {
  defaultBooleanFilter,
  defaultDateFilter,
  defaultDateTimeFilter,
  defaultNullFilter,
  defaultRelationFilter,
  defaultSelectFilter,
  defaultStringFilter,
  OPERATORS,
  type Filter,
} from '@/components/table-filters/constants';

import { Icons } from '@/components/icons';
import {
  RealEstateConfig,
  SubTenantConfig,
  SubTenantContractConnectionConfig,
  TenantConfig,
} from '@pigello/pigello-matrix';
import baseFilters, { baseTagFilter } from '../filters';

const subTenantFilters: Filter[] = [
  /* comment out for now until we sort out status
    {
     ...defaultEnumFilter,
    title: SubTenantConfig.fields.status.verboseName,
    externalFieldName: SubTenantConfig.fields.status.externalFieldName,
    enumChoices: SUB_TENANT_STATUSES,
  }, */
  {
    ...defaultRelationFilter,
    title: 'Kopplad hyresgäst',
    externalFieldName: 'connected_tenants',
    relationModelName: TenantConfig.modelName,
    icon: Icons.tenant,
    operators: [...OPERATORS.NOOP],
    defaultOperator: OPERATORS.NOOP[0],
  },
  {
    ...defaultSelectFilter,
    title: 'Typ av avtalskoppling',
    externalFieldName: 'stakeholder_type',
    choices:
      SubTenantContractConnectionConfig.fields.stakeholderType.choices ?? [],
    operators: [...OPERATORS.NOOP],
    defaultOperator: OPERATORS.NOOP[0],
  },
  {
    ...defaultStringFilter,
    title: SubTenantConfig.fields.customId.verboseName,
    externalFieldName: SubTenantConfig.fields.customId.externalFieldName,
  },
  {
    ...defaultNullFilter,
    title: SubTenantConfig.fields.isCompany.verboseName,
    externalFieldName: SubTenantConfig.fields.corporateName.externalFieldName,
  },
  {
    ...defaultStringFilter,
    title: SubTenantConfig.fields.corporateName.verboseName,
    externalFieldName: SubTenantConfig.fields.corporateName.externalFieldName,
    operators: [...OPERATORS.TEXT_CONTAINS, ...OPERATORS.TEXT_EXACT],
  },
  {
    ...defaultStringFilter,
    title: SubTenantConfig.fields.ssn.verboseName,
    externalFieldName: SubTenantConfig.fields.ssn.externalFieldName,
  },
  {
    ...defaultStringFilter,
    title: SubTenantConfig.fields.orgNo.verboseName,
    externalFieldName: SubTenantConfig.fields.orgNo.externalFieldName,
  },
  {
    ...defaultStringFilter,
    title: SubTenantConfig.fields.email.verboseName,
    externalFieldName: SubTenantConfig.fields.email.externalFieldName,
  },
  {
    ...defaultStringFilter,
    title: SubTenantConfig.fields.invoiceEmail.verboseName,
    externalFieldName: SubTenantConfig.fields.invoiceEmail.externalFieldName,
  },
  {
    ...defaultStringFilter,
    title: SubTenantConfig.fields.phoneNumber.verboseName,
    externalFieldName: SubTenantConfig.fields.phoneNumber.externalFieldName,
  },
  {
    ...defaultRelationFilter,
    title: SubTenantConfig.fields.realestates.verboseName,
    externalFieldName: SubTenantConfig.fields.realestates.externalFieldName,
    relationModelName: RealEstateConfig.modelName,
    icon: Icons.realestate,
  },
  {
    ...defaultStringFilter,
    title: SubTenantConfig.fields.username.verboseName,
    externalFieldName: SubTenantConfig.fields.username.externalFieldName,
  },
  {
    ...defaultStringFilter,
    title: SubTenantConfig.fields.sniNumber.verboseName,
    externalFieldName: SubTenantConfig.fields.sniNumber.externalFieldName,
  },
  {
    ...defaultBooleanFilter,
    title: SubTenantConfig.fields.ssnIsCoordinationNumber.verboseName,
    externalFieldName:
      SubTenantConfig.fields.ssnIsCoordinationNumber.externalFieldName,
  },
  {
    ...defaultBooleanFilter,
    title: SubTenantConfig.fields.isActive.verboseName,
    externalFieldName: SubTenantConfig.fields.isActive.externalFieldName,
  },
  {
    ...defaultDateFilter,
    title: SubTenantConfig.fields.birthDate.verboseName,
    externalFieldName: SubTenantConfig.fields.birthDate.externalFieldName,
  },
  {
    ...defaultDateFilter,
    title: SubTenantConfig.fields.latestActiveDate.verboseName,
    externalFieldName:
      SubTenantConfig.fields.latestActiveDate.externalFieldName,
  },
  {
    ...defaultDateFilter,
    title: SubTenantConfig.fields.earliestActiveDate.verboseName,
    externalFieldName:
      SubTenantConfig.fields.earliestActiveDate.externalFieldName,
    defaultOperator: OPERATORS.DATE_PAST[0],
  },
  {
    ...defaultDateTimeFilter,
    title: SubTenantConfig.fields.lastLogin.verboseName,
    externalFieldName: SubTenantConfig.fields.lastLogin.externalFieldName,
    defaultOperator: OPERATORS.DATE_TIME_PAST[0],
  },
  {
    ...defaultBooleanFilter,
    title: SubTenantConfig.fields.emailVerified.verboseName,
    externalFieldName: SubTenantConfig.fields.emailVerified.externalFieldName,
  },
  {
    ...defaultBooleanFilter,
    title: SubTenantConfig.fields.phoneNumberVerified.verboseName,
    externalFieldName:
      SubTenantConfig.fields.phoneNumberVerified.externalFieldName,
  },
  {
    ...defaultBooleanFilter,
    title: SubTenantConfig.fields.highRiskInteraction.verboseName,
    externalFieldName:
      SubTenantConfig.fields.highRiskInteraction.externalFieldName,
  },
  {
    ...defaultBooleanFilter,
    title: SubTenantConfig.fields.wantsEvicted.verboseName,
    externalFieldName:
      SubTenantConfig.fields.wantsEvicted.externalFieldName,
  },

  {
    ...defaultDateTimeFilter,
    title: SubTenantConfig.fields.activeTo.verboseName,
    externalFieldName: SubTenantConfig.fields.activeTo.externalFieldName,
  },

  {
    ...defaultStringFilter,
    title: `${SubTenantConfig.fields.invoiceAddress.verboseName} - Gatunamn`,
    externalFieldName: `${SubTenantConfig.fields.invoiceAddress.externalFieldName}__street`,
  },
  {
    ...defaultStringFilter,
    title: `${SubTenantConfig.fields.invoiceAddress.verboseName} - Stad`,
    externalFieldName: `${SubTenantConfig.fields.invoiceAddress.externalFieldName}__city`,
  },
  {
    ...defaultStringFilter,
    title: `${SubTenantConfig.fields.invoiceAddress.verboseName} - Gatunummer`,
    externalFieldName: `${SubTenantConfig.fields.invoiceAddress.externalFieldName}__street_number`,
  },
  {
    ...defaultStringFilter,
    title: `${SubTenantConfig.fields.invoiceAddress.verboseName} - Land`,
    externalFieldName: `${SubTenantConfig.fields.invoiceAddress.externalFieldName}__country`,
  },

  ...baseTagFilter,
  ...baseFilters,
];

export default subTenantFilters;
